<template>
  <b-card no-body class="border rounded mb-3">
    <b-card-body class="py-3">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h6 class="font-weight-bold mb-0">IV serum #{{ index + 1 }}</h6>
        <b-button variant="outline-danger" size="sm" @click="$emit('remove')">
          <i class="fas fa-trash-alt"></i>
        </b-button>
      </div>

      <b-form-group label="IV serum" class="mb-2">
        <treeselect
          :value="card.package_id"
          :options="serumOptions"
          :normalizer="serumNormalizer"
          placeholder="Select IV serum"
          @input="onSerumInput" />
      </b-form-group>
      <p v-if="serumDescription" class="text-muted small mb-2">{{ serumDescription }}</p>

      <b-form-group v-if="card.package_id" label="Extra vitamins" class="mb-2">
        <div v-if="loadingAddons" class="text-muted small mb-2">Loading facility prices…</div>
        <div v-else-if="!facilityAddons.length" class="text-muted small mb-2">
          No extra vitamins priced for this facility.
        </div>
        <template v-else>
          <div
            v-for="(row, rowIndex) in card.addons"
            :key="row._key"
            class="d-flex flex-wrap align-items-start mb-2">
            <div class="flex-grow-1 mr-2 mb-2">
              <treeselect
                :value="row.iv_addon_id"
                :options="addonOptionsFor(row)"
                :normalizer="addonNormalizer"
                placeholder="Select extra vitamin"
                @input="value => updateAddon(rowIndex, { iv_addon_id: value || null })" />
            </div>
            <div class="mr-2 mb-2" style="width: 6rem">
              <b-form-input
                :value="row.qty"
                type="number"
                min="1"
                step="1"
                placeholder="Qty"
                :disabled="!row.iv_addon_id"
                :state="qtyState(row)"
                @input="value => updateAddon(rowIndex, { qty: value })" />
            </div>
            <b-button variant="outline-danger" size="sm" class="mb-2" @click="removeAddon(rowIndex)">
              <i class="fas fa-times"></i>
            </b-button>
          </div>
          <b-button variant="outline-primary" size="sm" @click="addAddon">
            <i class="fas fa-plus mr-1"></i> Add extra vitamin
          </b-button>
        </template>
      </b-form-group>

      <div v-if="card.package_id" class="border-top pt-2 mt-2 small">
        <div class="d-flex justify-content-between mb-1">
          <span>{{ serumName }}</span>
          <span>${{ formatMoney(serumPrice) }}</span>
        </div>
        <div
          v-for="addon in quotedAddons"
          :key="addon.iv_addon_id"
          class="d-flex justify-content-between mb-1">
          <span>{{ addon.name }} × {{ addon.qty }}</span>
          <span>${{ addon.line_total }}</span>
        </div>
        <div class="d-flex justify-content-between">
          <strong>Line total</strong>
          <strong>${{ line ? line.line_total : formatMoney(serumPrice) }}</strong>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardBody, BButton, BFormGroup, BFormInput } from 'bootstrap-vue'
import { packagePrice, parseIntegerQty, formatCatalogPrice } from '@/core/utils/ivTherapies'

let addonRowKey = 0

/** Tarjeta de un suero IV del paso 7: su suero, sus vitaminas y su precio cotizado. */
export default {
  name: 'IvSerumCard',
  components: { BCard, BCardBody, BButton, BFormGroup, BFormInput },
  props: {
    card: { type: Object, required: true },
    index: { type: Number, required: true },
    serumOptions: { type: Array, default: () => [] },
    facilityAddons: { type: Array, default: () => [] },
    loadingAddons: { type: Boolean, default: false },
    line: { type: Object, default: null },
  },
  computed: {
    selectedSerum() {
      return this.serumOptions.find(item => Number(item.id) === Number(this.card.package_id)) || null
    },
    serumName() {
      return (this.line && this.line.name) || (this.selectedSerum && this.selectedSerum.name) || 'IV serum'
    },
    serumDescription() {
      return (this.selectedSerum && this.selectedSerum.description) || ''
    },
    serumPrice() {
      if (this.line && this.line.price != null) return Number(this.line.price) || 0
      return packagePrice(this.selectedSerum)
    },
    quotedAddons() {
      return (this.line && this.line.addons) || []
    },
  },
  methods: {
    formatMoney(value) {
      const n = Number(value)
      return Number.isFinite(n) ? n.toFixed(2) : '0.00'
    },
    serumNormalizer(node) {
      const price = formatCatalogPrice(node.price)
      return { id: node.id, label: price ? `${node.name} — $${price}` : node.name }
    },
    addonNormalizer(node) {
      const price = formatCatalogPrice(node.catalogPrice)
      return {
        id: node.id,
        label: price ? `${node.name} — $${price}` : node.name,
        isDisabled: !!node.isDisabled,
      }
    },
    addonOptionsFor(row) {
      const taken = new Set(
        this.card.addons
          .filter(item => item._key !== row._key && item.iv_addon_id)
          .map(item => Number(item.iv_addon_id))
      )
      const options = this.facilityAddons.map(addon => ({ ...addon, isDisabled: taken.has(Number(addon.id)) }))
      if (row.iv_addon_id && !options.some(addon => Number(addon.id) === Number(row.iv_addon_id))) {
        options.push({ id: Number(row.iv_addon_id), name: row.name || `Add-on #${row.iv_addon_id}` })
      }
      return options
    },
    qtyState(row) {
      if (!row.iv_addon_id) return null
      return parseIntegerQty(row.qty) == null ? false : null
    },
    emitCard(changes) {
      this.$emit('change', { ...this.card, ...changes })
    },
    onSerumInput(id) {
      const changes = { package_id: id || null }
      if (id && !this.card.addons.length && this.facilityAddons.length) {
        changes.addons = [this.newAddonRow()]
      }
      this.emitCard(changes)
    },
    newAddonRow() {
      return { _key: `iv-addon-${++addonRowKey}`, iv_addon_id: null, qty: 1 }
    },
    addAddon() {
      this.emitCard({ addons: [...this.card.addons, this.newAddonRow()] })
    },
    removeAddon(rowIndex) {
      this.emitCard({ addons: this.card.addons.filter((_, i) => i !== rowIndex) })
    },
    updateAddon(rowIndex, changes) {
      const addons = this.card.addons.map((row, i) => (i === rowIndex ? { ...row, ...changes } : row))
      this.emitCard({ addons })
    },
  },
}
</script>
